import { useState, useEffect } from 'react';
import {
  TextField,
  Chip,
  Autocomplete,
  Avatar,
  Box,
  Typography,
  TextFieldProps
} from '@mui/material';
import { fetchAgencyOfficials, OfficialData } from '../../../networkRequest/officialsService';
import { useUser } from '../../../hooks/useUser';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';

type EmailInviteChipProps = TextFieldProps & {
  initRecipients: OfficialData[];
  onRecipientsChange: (recipients: OfficialData[]) => void;
};

const getLabel = (opt: OfficialData) => `${opt.first_name} ${opt.last_name}`;

export const EmailInviteChips = ({
  initRecipients,
  onRecipientsChange,
  ...props
}: EmailInviteChipProps) => {
  const { user } = useUser();
  const [recipients, setRecipients] = useState<OfficialData[]>(initRecipients);
  const [options, setOptions] = useState<OfficialData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const debouncedInputValue = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    if (!user?.accessToken || !user?.profile?.agency_id) return;
    const loadOptions = async () => {
      setLoading(true);
      try {
        const { data, error: fetchError } = await fetchAgencyOfficials({
          accessToken: user.accessToken,
          agencyId: user.profile.agency_id,
          query: debouncedInputValue || undefined,
        });
        if (fetchError) {
          setError(fetchError);
          return;
        }
        if (data) setOptions(data);
      } catch (e) {
        setError('Failed to load recipients.');
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [user?.accessToken, user?.profile?.agency_id, debouncedInputValue]);

  const handleChange = (_event: unknown, selectedOptions: OfficialData[]) => {
    setRecipients(selectedOptions);
    onRecipientsChange(selectedOptions);
  };

  return (
    <Autocomplete
      multiple
      disableClearable
      disableCloseOnSelect
      filterSelectedOptions
      forcePopupIcon={false}
      disabled={!!error}
      options={options}
      loading={loading}
      value={recipients}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      filterOptions={(x) => x}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => getLabel(option)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={handleChange}
      renderValue={(tagValue, getItemProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getItemProps({ index })}
            key={option.id}
            label={getLabel(option)}
            icon={<Avatar sx={{ width: 24, height: 24 }} src={option.img_url} alt="user avatar" />}
            sx={{ backgroundColor: 'white', border: '1px solid rgba(0, 0, 0, 0.42)' }}
          />
        ))
      }
      renderOption={({ key, ...optionProps }, option) => (
        <Box
          key={key}
          component="li"
          {...optionProps}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            gap: '8px'
          }}
        >
          <Avatar sx={{ width: 32, height: 32 }} src={option.img_url} alt="user avatar" />
          <Typography>{getLabel(option)}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...props}
          label="To"
          placeholder={recipients.length ? '' : 'Select recipients'}
          sx={{ marginTop: '0.5em' }}
          error={!!error}
          helperText={error || undefined}
        />
      )}
    />
  );
};

export default EmailInviteChips;
